import { Alert, Button, Card, Checkbox, Form, Select, Space, Table } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type TeacherSlot = {
  id: string;
  day_of_week: number;
  period_code?: string;
  period_name?: string;
  subject_code?: string;
  subject_name?: string;
  section_name?: string;
  grade_code?: string;
  room_code?: string;
  grid_status?: string;
};

type SectionTeacher = {
  staff_link_id: string;
  employee_code?: string;
  first_name?: string;
  last_name?: string;
};

export default function TimetableTeacherPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.timetable.manage') || user?.permissions?.includes('education.admin')
  );
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [staffLinkId, setStaffLinkId] = useState<string | undefined>();
  const [includeDraft, setIncludeDraft] = useState(false);

  const teachers = useQuery({
    queryKey: ['education-section-teachers', sessionId],
    enabled: Boolean(sessionId),
    queryFn: async () => {
      const res = await api.get<SectionTeacher[]>('/education/academic/section-teachers', {
        params: { session_id: sessionId },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const staffOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: Array<{ value: string; label: string }> = [];
    for (const row of teachers.data ?? []) {
      if (!row.staff_link_id || seen.has(row.staff_link_id)) continue;
      seen.add(row.staff_link_id);
      opts.push({
        value: row.staff_link_id,
        label: `${row.employee_code || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`.trim(),
      });
    }
    return opts;
  }, [teachers.data]);

  const slots = useQuery({
    queryKey: ['education-timetable-teacher-slots', sessionId, staffLinkId, includeDraft],
    enabled: Boolean(sessionId) && Boolean(staffLinkId),
    queryFn: async () => {
      const res = await api.get<TeacherSlot[]>('/education/timetable/teacher-slots', {
        params: {
          session_id: sessionId,
          staff_link_id: staffLinkId,
          include_draft: includeDraft ? 'true' : undefined,
        },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('timetableTeacherView')}
      description={t('timetableTeacherViewIntro')}
      extra={<Button onClick={() => navigate('/education/timetable')}>{t('backToTimetable')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline">
            <Form.Item label={t('session')} required>
              <EducationSessionSelect
                style={{ minWidth: 200 }}
                value={sessionId}
                onChange={(v) => {
                  setSessionId(v || undefined);
                  setStaffLinkId(undefined);
                }}
              />
            </Form.Item>
            <Form.Item label={t('teacher')} required>
              <Select
                style={{ minWidth: 220 }}
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={!sessionId}
                value={staffLinkId}
                onChange={(v) => setStaffLinkId(v || undefined)}
                options={staffOptions}
                loading={teachers.isFetching}
              />
            </Form.Item>
            {canManage ? (
              <Form.Item>
                <Checkbox checked={includeDraft} onChange={(e) => setIncludeDraft(e.target.checked)}>
                  {t('includeDraftTimetables')}
                </Checkbox>
              </Form.Item>
            ) : null}
          </Form>
        </Card>
        {!sessionId || !staffLinkId ? (
          <Alert type="info" showIcon message={t('timetablePickSessionTeacher')} />
        ) : (
          <Card size="small">
            <Table
              rowKey="id"
              size="small"
              loading={slots.isLoading}
              dataSource={slots.data ?? []}
              pagination={{ pageSize: 50 }}
              columns={[
                {
                  title: t('day'),
                  dataIndex: 'day_of_week',
                  width: 100,
                  render: (d: number) => t(`weekday${d}` as 'weekday1'),
                },
                {
                  title: t('period'),
                  render: (_: unknown, row: TeacherSlot) => `${row.period_code || ''} — ${row.period_name || ''}`,
                },
                {
                  title: t('subject'),
                  render: (_: unknown, row: TeacherSlot) => `${row.subject_code || ''} — ${row.subject_name || ''}`,
                },
                {
                  title: t('section'),
                  render: (_: unknown, row: TeacherSlot) =>
                    `${row.grade_code ? `${row.grade_code} ` : ''}${row.section_name || ''}`,
                },
                { title: t('room'), dataIndex: 'room_code', render: (v) => v || '—' },
                { title: t('status'), dataIndex: 'grid_status', width: 110 },
              ]}
            />
          </Card>
        )}
      </Space>
    </EducationPageChrome>
  );
}
